import { useState, useEffect, useRef } from 'react';
import { adminApi } from '../../api';
import api from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ScrollArea } from '../../components/ui/scroll-area';
import { MessageCircle, Send, Loader2, User, Trash2, Bot, Sparkles, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react';

const formatTime = (date) => new Date(date).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });

export default function AdminChat() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [smartsuppConfigured, setSmartsuppConfigured] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { 
    fetchUsers(); 
    checkSmartsuppConfig();
  }, []);
  useEffect(() => { if (selectedUser) fetchMessages(); }, [selectedUser]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const checkSmartsuppConfig = async () => {
    try {
      const response = await api.get('/settings/smartsupp');
      setSmartsuppConfigured(!!response.data?.smartsupp_key);
    } catch (error) {
      setSmartsuppConfigured(false);
    }
  };

  const fetchUsers = async () => {
    try { const response = await adminApi.getChatUsers(); setUsers(response.data); } 
    catch (error) { console.error('Failed to fetch users:', error); } 
    finally { setIsLoading(false); }
  };

  const fetchMessages = async () => {
    try { const response = await adminApi.getChatMessages(selectedUser.id); setMessages(response.data); }
    catch (error) { console.error('Failed to fetch messages:', error); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedUser) return;
    setSending(true);
    try { await adminApi.sendChatMessage(selectedUser.id, message); setMessage(''); fetchMessages(); }
    catch (error) { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  const handleDeleteUserChat = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;
    
    setDeleting(true);
    try {
      await adminApi.deleteChat(userId);
      toast.success('Conversation deleted');
      setUsers(users.filter(u => u.id !== userId));
      if (selectedUser?.id === userId) {
        setSelectedUser(null);
        setMessages([]);
      }
    } catch (error) {
      toast.error('Failed to delete conversation');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAllChats = async () => {
    if (!window.confirm('Are you sure you want to delete ALL chat conversations? This cannot be undone.')) return;
    
    setDeleting(true);
    try {
      await adminApi.deleteAllChats();
      toast.success('All conversations deleted');
      setUsers([]);
      setSelectedUser(null);
      setMessages([]);
    } catch (error) {
      toast.error('Failed to delete conversations');
    } finally {
      setDeleting(false);
    }
  };

  const generateAIResponse = async () => {
    if (messages.length === 0) {
      toast.error('No messages to generate response for');
      return;
    }
    
    setAiGenerating(true);
    try {
      const response = await api.post('/admin/chat/ai-response', {
        user_id: selectedUser.id,
        context: messages.slice(-10).map(m => ({
          role: m.is_admin ? 'assistant' : 'user',
          content: m.content
        }))
      });
      
      if (response.data?.suggestion) {
        setMessage(response.data.suggestion);
        toast.success('AI suggestion generated');
      }
    } catch (error) {
      toast.error('Failed to generate AI response');
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Live Chat</h1>
          <p className="text-zinc-400 text-sm">Manage customer support conversations</p>
        </div>
      </div>

      {/* Smartsupp Info Card */}
      <Card className="bg-gradient-to-r from-[#FFD700]/10 to-[#FFA500]/10 border-[#FFD700]/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-[#FFD700]" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold mb-1">Smartsupp Live Chat Integration</h3>
              {smartsuppConfigured ? (
                <>
                  <p className="text-zinc-400 text-sm mb-3">
                    Your Smartsupp live chat is active! Users can now chat with you from anywhere on your site.
                    Reply to customers from the Smartsupp dashboard or mobile app.
                  </p>
                  <div className="flex gap-2">
                    <a 
                      href="https://app.smartsupp.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-black rounded-lg font-medium hover:bg-[#E5C100] transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open Smartsupp Dashboard
                    </a>
                    <a 
                      href="https://www.smartsupp.com/apps" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                    >
                      Get Mobile App
                    </a>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-amber-500 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Smartsupp not configured</span>
                  </div>
                  <p className="text-zinc-400 text-sm mb-3">
                    Set up Smartsupp to chat with customers in real-time from any device.
                    Go to Settings → Live Chat (Smartsupp) to add your key.
                  </p>
                  <a 
                    href="/admin/settings" 
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-black rounded-lg font-medium hover:bg-[#E5C100] transition-colors"
                  >
                    Configure Smartsupp
                  </a>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legacy Chat Section */}
      {users.length > 0 && (
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-white text-lg">Legacy Chat Messages</CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleDeleteAllChats}
              disabled={deleting}
              className="border-red-500/30 text-red-500 hover:bg-red-500/10"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Delete All
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <p className="px-4 pb-3 text-xs text-zinc-500">
              These are messages from the old in-app chat system. New conversations happen via Smartsupp.
            </p>
            <div className="grid md:grid-cols-3 h-[500px] border-t border-white/10">
              {/* Users List */}
              <div className="border-r border-white/10">
                <ScrollArea className="h-full">
                  {isLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#FFD700]" />
                    </div>
                  ) : users.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-zinc-500">No legacy chats</p>
                    </div>
                  ) : (
                    users.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => setSelectedUser(user)}
                        className={`flex items-center justify-between p-3 cursor-pointer hover:bg-white/5 transition-colors ${
                          selectedUser?.id === user.id ? 'bg-[#FFD700]/10 border-l-2 border-[#FFD700]' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#121212] flex items-center justify-center">
                            <User className="w-5 h-5 text-zinc-400" />
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{user.username}</p>
                            <p className="text-zinc-500 text-xs">{user.email}</p>
                          </div>
                        </div>
                        {user.unread_count > 0 && (
                          <span className="bg-[#FFD700] text-black text-xs font-bold px-2 py-1 rounded-full">
                            {user.unread_count}
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </ScrollArea>
              </div>

              {/* Chat Area */}
              <div className="md:col-span-2 flex flex-col">
                {selectedUser ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-3 border-b border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#121212] flex items-center justify-center">
                          <User className="w-4 h-4 text-zinc-400" />
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{selectedUser.username}</p>
                          <p className="text-zinc-500 text-xs">{selectedUser.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchMessages()}
                          className="text-zinc-400 hover:text-white"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUserChat(selectedUser.id)}
                          disabled={deleting}
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Messages */}
                    <ScrollArea className="flex-1 p-4">
                      <div className="space-y-3">
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.is_admin ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-lg p-3 ${
                                msg.is_admin
                                  ? 'bg-[#FFD700]/20 border border-[#FFD700]/30'
                                  : 'bg-[#121212]'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-medium ${msg.is_admin ? 'text-[#FFD700]' : 'text-zinc-400'}`}>
                                  {msg.is_admin ? 'You' : selectedUser.username}
                                </span>
                                <span className="text-xs text-zinc-600">{formatTime(msg.created_at)}</span>
                              </div>
                              <p className="text-white text-sm">{msg.content}</p>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    </ScrollArea>

                    {/* Input */}
                    <form onSubmit={handleSend} className="p-3 border-t border-white/10 flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={generateAIResponse}
                        disabled={aiGenerating || messages.length === 0}
                        className="text-[#FFD700] hover:bg-[#FFD700]/10"
                        title="Generate AI response"
                      >
                        {aiGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      </Button>
                      <Input
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="bg-[#121212] border-white/10 text-white"
                      />
                      <Button
                        type="submit"
                        disabled={sending}
                        className="bg-[#FFD700] text-black hover:bg-[#E5C100]"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </form>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <MessageCircle className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                      <p className="text-zinc-500">Select a conversation</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No legacy chats message */}
      {!isLoading && users.length === 0 && (
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="py-12 text-center">
            <MessageCircle className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">No Legacy Chat Messages</h3>
            <p className="text-zinc-500 text-sm max-w-md mx-auto">
              All new customer conversations will happen through Smartsupp. 
              {smartsuppConfigured 
                ? " Open your Smartsupp dashboard to see and reply to messages."
                : " Configure Smartsupp in Settings to enable live chat."
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
